import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n";
import SystemDiagram from "./assets/system-diagram.png";
import TapkeyLock from "./assets/tapkey-lock.png";
import TapkeyLockSilver from "./assets/tapkey-lock-silver.png";
import ControlKit from "./assets/control-kit.png";
import RelayKit from "./assets/relay-kit.png";
import PaytLock from "./assets/payt-lock.jpg";
import DailyPhoto from "./assets/schliesssysteme-alltag.png";

const kicker = "text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu";
const h2Class =
  "text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink";
const leadClass =
  "text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted";

const Check = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    className="mt-px h-5 w-5 flex-shrink-0 text-juhuu"
  >
    <path d="m5 12 5 5L20 7" />
  </svg>
);

const UseCaseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-[18px] w-[18px]"
  >
    <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const BoltIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-[18px] w-[18px]"
  >
    <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
  </svg>
);

const EXPLAINER_ICONS = [
  <>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>,
  <>
    <rect x="2" y="7" width="20" height="10" rx="2" />
    <path d="M6 11v2M10 11v2M14 11v2M18 11v2" />
  </>,
  <>
    <rect x="7" y="2" width="10" height="20" rx="2" />
    <path d="M11 18h2" />
  </>,
];

const ACCESSORY_ICONS = [
  <>
    <rect x="3" y="3" width="18" height="14" rx="2" />
    <path d="M8 21h8M12 17v4M7 7h10M7 11h6" />
  </>,
  <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 10h6M7 14h4" />
    <circle cx="17.5" cy="12" r="1.5" />
  </>,
  <>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    <circle cx="12" cy="12" r="3" />
  </>,
];

/** Tapkey stage — auto-rotates between the two cylinder variants. */
function TapkeyStage({ alt }: { alt: string }) {
  const [i, setI] = useState(0);
  const images = [TapkeyLock, TapkeyLockSilver];

  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduce) return;
    const timer = setInterval(() => setI((p) => (p + 1) % images.length), 2600);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="relative flex min-h-[340px] items-center justify-center overflow-hidden rounded-[28px] border border-line bg-gradient-to-br from-white to-bg-alt p-6">
      {images.map((src, idx) => (
        <img
          key={idx}
          src={src}
          alt={alt}
          className={`absolute inset-0 h-full w-full object-contain p-6 drop-shadow-[0_20px_34px_rgba(20,18,31,.20)] transition-opacity duration-700 ${
            idx === i ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );
}

/** Static product stage on a plain white background. */
function KitStageWhite({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="flex min-h-[340px] items-center justify-center overflow-hidden rounded-[28px] border border-line bg-white p-6">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="max-h-[540px] w-auto max-w-full object-contain"
      />
    </div>
  );
}

function KitSpecs({ specs }: { specs: { k: string; v: string }[] }) {
  return (
    <div className="mt-6 overflow-hidden rounded-[14px] border border-line">
      {specs.map((row, i) => (
        <div
          key={i}
          className="grid grid-cols-[.85fr_1.15fr] border-b border-line last:border-b-0"
        >
          <div className="px-4 py-[.7rem] text-[.85rem] font-bold text-ink">
            {row.k}
          </div>
          <div className="border-l border-line px-4 py-[.7rem] text-[.85rem] text-muted">
            {row.v}
          </div>
        </div>
      ))}
    </div>
  );
}

function KitNote({
  variant,
  label,
  text,
}: {
  variant: "uc" | "pnp";
  label: string;
  text: string;
}) {
  const isUc = variant === "uc";
  return (
    <div
      className={`mt-5 flex gap-3.5 rounded-[14px] p-[1.1rem] ${
        isUc
          ? "border border-juhuu-soft-2 bg-juhuu-soft"
          : "border border-line bg-bg-alt"
      }`}
    >
      <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-[10px] bg-white text-juhuu">
        {isUc ? <UseCaseIcon /> : <BoltIcon />}
      </div>
      <div>
        <div
          className={`mb-1 text-[.7rem] font-extrabold uppercase tracking-[.08em] ${
            isUc ? "text-juhuu-deep" : "text-ink"
          }`}
        >
          {label}
        </div>
        <p
          className={`text-[.9rem] leading-[1.55] ${
            isUc ? "text-ink-2" : "text-muted"
          }`}
        >
          {text}
        </p>
      </div>
    </div>
  );
}

export default function NewLockingSystemsRoute() {
  const { t } = useTranslation();

  const list = (base: string, n: number) =>
    Array.from({ length: n }, (_, i) => t(`${base}.${i}`));

  const specsFor = (kitIndex: number, count: number) =>
    Array.from({ length: count }, (_, i) => ({
      k: t(`routes.lockingSystems.kits.${kitIndex}.specs.${i}.k`),
      v: t(`routes.lockingSystems.kits.${kitIndex}.specs.${i}.v`),
    }));

  const KIT_SPEC_COUNTS = [11, 7, 8, 7];

  const kitHeader = (i: number, badgeClass: string) => (
    <div className="mb-3.5 flex items-center gap-3">
      <span
        className={`inline-flex items-center rounded-full px-3 py-[.34rem] text-[.7rem] font-extrabold uppercase tracking-[.05em] ${badgeClass}`}
      >
        {t(`routes.lockingSystems.kits.${i}.badge`)}
      </span>
      <span className="text-[.85rem] text-muted">
        {t(`routes.lockingSystems.kits.${i}.index`)}
      </span>
    </div>
  );

  const kitTitle = (i: number) => (
    <h3 className="text-[clamp(1.4rem,2.2vw,2rem)] font-extrabold tracking-[-0.02em] text-ink">
      {t(`routes.lockingSystems.kits.${i}.title`)}
    </h3>
  );

  const kitDescription = (i: number) => (
    <p className="mt-3 text-base leading-[1.65] text-muted">
      {t(`routes.lockingSystems.kits.${i}.description`)}
    </p>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <main>
        {/* ── Hero + system diagram ────────────────────────── */}
        <section className="relative overflow-hidden px-4 md:px-8 pt-32 pb-[clamp(2.5rem,5vw,4rem)]">
          <div className="pointer-events-none absolute -top-44 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-juhuu-glow blur-[90px]" />
          <div className="relative mx-auto max-w-7xl">
            <div className="mx-auto max-w-[780px] text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-juhuu-soft-2 bg-juhuu-soft px-3.5 py-1.5 text-sm font-bold text-juhuu-deep">
                <span className="h-[7px] w-[7px] rounded-full bg-juhuu" />
                {t("routes.lockingSystems.heroBadge")}
              </div>
              <h1 className="mt-5 text-[clamp(2.3rem,4.6vw,3.9rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink">
                {t("routes.lockingSystems.heroTitleLine1")}
                <br />
                <span className="text-juhuu">
                  {t("routes.lockingSystems.heroTitleHighlight")}
                </span>{" "}
                {t("routes.lockingSystems.heroTitleSuffix")}
              </h1>
              <p className={`mx-auto mt-5 max-w-[600px] ${leadClass}`}>
                {t("routes.lockingSystems.heroDescription")}
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link
                  to="/meeting"
                  className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
                >
                  {t("routes.lockingSystems.ctaAdvice")}
                </Link>
                <a
                  href="#kits"
                  className="inline-flex items-center rounded-full border border-line-strong px-7 py-3.5 font-semibold text-ink transition-colors hover:border-juhuu hover:text-juhuu"
                >
                  {t("routes.lockingSystems.ctaSeeKits")}
                </a>
              </div>
            </div>

            {/* system diagram */}
            <div className="mt-[clamp(2.4rem,4vw,3.4rem)]">
              <div className="relative overflow-hidden rounded-[28px] border border-line bg-white p-[clamp(1rem,3vw,2.4rem)] shadow-lg">
                <div
                  className="pointer-events-none absolute inset-0 opacity-55"
                  style={{
                    background:
                      "radial-gradient(120% 90% at 50% 0%, #f1ebff 0%, transparent 55%)",
                  }}
                />
                <img
                  src={SystemDiagram}
                  alt={t("routes.lockingSystems.diagramAlt")}
                  loading="lazy"
                  className="relative block h-auto w-full rounded-2xl"
                />
              </div>

              <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-line">
                <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="bg-white px-[1.35rem] py-[1.3rem]">
                      <span className="text-[.72rem] font-extrabold tracking-[.04em] text-juhuu tabular-nums">
                        {`0${i + 1}`}
                      </span>
                      <div className="my-2 text-base font-extrabold tracking-[-0.01em] text-ink">
                        {t(`routes.lockingSystems.sysNodes.${i}.title`)}
                      </div>
                      <p className="text-[.83rem] leading-[1.55] text-muted">
                        {t(`routes.lockingSystems.sysNodes.${i}.description`)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Explainer ────────────────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-[680px]">
              <p className={kicker}>
                {t("routes.lockingSystems.explainerKicker")}
              </p>
              <h2 className={`mt-4 ${h2Class}`}>
                {t("routes.lockingSystems.explainerTitleLine1")}
                <br />
                {t("routes.lockingSystems.explainerTitleLine2")}
              </h2>
              <p className={`mt-4 ${leadClass}`}>
                {t("routes.lockingSystems.explainerLead")}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {[0, 1, 2].map((i) => {
                const filled = i === 1;
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-line bg-white p-8 shadow-sm"
                  >
                    <div
                      className={`mb-[1.1rem] grid h-12 w-12 place-items-center rounded-[13px] ${
                        filled
                          ? "bg-juhuu text-white"
                          : "border border-juhuu-soft-2 bg-juhuu-soft text-juhuu"
                      }`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="h-6 w-6"
                      >
                        {EXPLAINER_ICONS[i]}
                      </svg>
                    </div>
                    <h3 className="text-[1.12rem] font-bold text-ink">
                      {t(`routes.lockingSystems.explainerCards.${i}.title`)}
                    </h3>
                    <p className="mt-2.5 text-[.92rem] leading-[1.6] text-muted">
                      {t(
                        `routes.lockingSystems.explainerCards.${i}.description`
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Kits ─────────────────────────────────────────── */}
        <section id="kits" className="scroll-mt-20 px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-[3.2rem] max-w-[640px] text-center">
              <p className={kicker}>{t("routes.lockingSystems.kitsKicker")}</p>
              <h2 className={`mt-4 ${h2Class}`}>
                {t("routes.lockingSystems.kitsTitle")}
              </h2>
            </div>

            {/* 01 — Tapkey */}
            <div className="mb-[clamp(3rem,6vw,5rem)] grid items-center gap-12 lg:grid-cols-2">
              <TapkeyStage alt={t("routes.lockingSystems.kits.0.title")} />
              <div>
                {kitHeader(0, "bg-juhuu-soft text-juhuu-deep")}
                {kitTitle(0)}
                {kitDescription(0)}
                <KitSpecs specs={specsFor(0, KIT_SPEC_COUNTS[0])} />
                <KitNote
                  variant="uc"
                  label={t("routes.lockingSystems.labelUseCase")}
                  text={t("routes.lockingSystems.kits.0.useCase")}
                />
              </div>
            </div>

            {/* 02 — Control Kit (order-flip) */}
            <div className="mb-[clamp(3rem,6vw,5rem)] grid items-center gap-12 lg:grid-cols-2">
              <div className="order-2 lg:order-1">
                {kitHeader(1, "bg-ink text-white")}
                {kitTitle(1)}
                {kitDescription(1)}
                <KitSpecs specs={specsFor(1, KIT_SPEC_COUNTS[1])} />
                <KitNote
                  variant="pnp"
                  label={t("routes.lockingSystems.labelPlugAndPlay")}
                  text={t("routes.lockingSystems.plugAndPlayText")}
                />
                <KitNote
                  variant="uc"
                  label={t("routes.lockingSystems.labelUseCase")}
                  text={t("routes.lockingSystems.kits.1.useCase")}
                />
              </div>
              <div className="order-1 lg:order-2">
                <KitStageWhite
                  src={ControlKit}
                  alt={t("routes.lockingSystems.kits.1.title")}
                />
              </div>
            </div>

            {/* 03 — Relay Kit */}
            <div className="mb-[clamp(3rem,6vw,5rem)] grid items-center gap-12 lg:grid-cols-2">
              <KitStageWhite
                src={RelayKit}
                alt={t("routes.lockingSystems.kits.2.title")}
              />
              <div>
                {kitHeader(2, "bg-ink text-white")}
                {kitTitle(2)}
                {kitDescription(2)}
                <KitSpecs specs={specsFor(2, KIT_SPEC_COUNTS[2])} />
                <KitNote
                  variant="pnp"
                  label={t("routes.lockingSystems.labelPlugAndPlay")}
                  text={t("routes.lockingSystems.plugAndPlayText")}
                />
                <KitNote
                  variant="uc"
                  label={t("routes.lockingSystems.labelUseCase")}
                  text={t("routes.lockingSystems.kits.2.useCase")}
                />
              </div>
            </div>

            {/* 04 — EMZ (order-flip) */}
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div className="order-2 lg:order-1">
                {kitHeader(3, "bg-juhuu-soft text-juhuu-deep")}
                {kitTitle(3)}
                {kitDescription(3)}
                <KitSpecs specs={specsFor(3, KIT_SPEC_COUNTS[3])} />
                <KitNote
                  variant="uc"
                  label={t("routes.lockingSystems.labelUseCase")}
                  text={t("routes.lockingSystems.kits.3.useCase")}
                />
              </div>
              <div className="order-1 lg:order-2">
                <KitStageWhite
                  src={PaytLock}
                  alt={t("routes.lockingSystems.kits.3.title")}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Accessories ──────────────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-[680px]">
              <p className={kicker}>
                {t("routes.lockingSystems.accessoriesKicker")}
              </p>
              <h2 className={`mt-4 ${h2Class}`}>
                {t("routes.lockingSystems.accessoriesTitle")}
              </h2>
              <p className={`mt-4 ${leadClass}`}>
                {t("routes.lockingSystems.accessoriesLead")}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-line bg-white p-8 shadow-sm"
                >
                  <div className="mb-[1.1rem] grid h-[50px] w-[50px] place-items-center rounded-[14px] bg-juhuu text-white">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="h-6 w-6"
                    >
                      {ACCESSORY_ICONS[i]}
                    </svg>
                  </div>
                  <h3 className="text-[1.18rem] font-bold text-ink">
                    {t(`routes.lockingSystems.accessories.${i}.title`)}
                  </h3>
                  <p className="mt-2.5 text-[.94rem] leading-[1.65] text-muted">
                    {t(`routes.lockingSystems.accessories.${i}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Built for daily use ──────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div>
              <p className={kicker}>{t("routes.lockingSystems.dailyKicker")}</p>
              <h2 className="mt-3 text-[clamp(1.6rem,2.5vw,2.4rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.lockingSystems.dailyTitleLine1")}
                <br />
                {t("routes.lockingSystems.dailyTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.lockingSystems.dailyDescription")}
              </p>
              <div className="flex flex-col gap-3">
                {list("routes.lockingSystems.dailyBullets", 4).map((b, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check />
                    <span className="text-[.95rem] text-ink-2">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Photo stage — on-site use */}
            <div className="overflow-hidden rounded-[28px] border border-line bg-bg-alt p-6">
              <img
                src={DailyPhoto}
                alt={t("routes.lockingSystems.dailyPhotoAlt")}
                loading="lazy"
                className="block h-auto w-full object-contain"
              />
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20 text-center">
          <div className="mx-auto max-w-[620px]">
            <h2 className={h2Class}>
              {t("routes.lockingSystems.finalCtaTitle")}
            </h2>
            <p className={`mx-auto my-6 max-w-[460px] ${leadClass}`}>
              {t("routes.lockingSystems.finalCtaDescription")}
            </p>
            <Link
              to="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("routes.lockingSystems.finalCtaButton")}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
